import { createContext, useCallback, useContext, useSyncExternalStore } from "react";

export const EXPERT_MODE_STORAGE_KEY = "optivo-ui-expert-mode";

/**
 * Consumers that have to agree the moment the switch is flipped — the sidebar
 * step list and the wizard body are siblings in the tree, so a plain `useState`
 * per consumer would leave the rail listing steps the wizard no longer walks.
 */
const listeners = new Set<() => void>();

function readStoredPreference(): boolean {
    if (typeof window === "undefined") return false;

    try {
        return window.localStorage.getItem(EXPERT_MODE_STORAGE_KEY) === "true";
    } catch {
        return false;
    }
}

function writeStoredPreference(expert: boolean): void {
    if (typeof window === "undefined") return;

    try {
        window.localStorage.setItem(EXPERT_MODE_STORAGE_KEY, expert ? "true" : "false");
    } catch {
        // Non-critical: the mode still switches for this session without storage.
    }
}

function subscribe(onStoreChange: () => void): () => void {
    listeners.add(onStoreChange);
    // A second tab switching the mode should not leave this one out of step.
    window.addEventListener("storage", onStoreChange);
    return () => {
        listeners.delete(onStoreChange);
        window.removeEventListener("storage", onStoreChange);
    };
}

/**
 * A subtree the operator has lifted onto the expert tier without moving the
 * stored preference — one section saying "I'm an expert" rather than the whole
 * surface. Provided by `ExpertModeScope`; read here so the shared controls keep
 * reading the tier for themselves and the two surfaces cannot drift apart.
 */
export const ExpertModeScopeContext = createContext(false);

/**
 * Whether the surface this subtree belongs to offers the expert tier at all.
 * Provided by `ExpertModeAvailability`. A ceiling rather than a lift: where it
 * is false, neither the stored preference nor a section's own scope brings the
 * expert tier in — the setup wizard walks the essential path alone for now,
 * while the same preference goes on serving the strategy editor.
 */
export const ExpertModeAvailableContext = createContext(true);

/**
 * Whether the settings surfaces show the expert tier.
 *
 * Off by default: a first pass through the wizard, and a freshly opened
 * strategy, should read as the decisions that have to be made rather than
 * every dial the engine exposes. The choice is remembered across surfaces and
 * sessions, because it describes the operator rather than the screen.
 */
export function useExpertMode(): [boolean, (expert: boolean) => void] {
    const stored = useSyncExternalStore(subscribe, readStoredPreference, () => false);
    const scoped = useContext(ExpertModeScopeContext);
    const available = useContext(ExpertModeAvailableContext);

    // The setter always speaks for the preference, never for the scope: a
    // section revealing its own dials must not switch the mode on everywhere.
    const setExpertMode = useCallback((next: boolean) => {
        writeStoredPreference(next);
        for (const listener of listeners) listener();
    }, []);

    return [available && (stored || scoped), setExpertMode];
}
